import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Star, X, ArrowRight } from "lucide-react";
import { useCasting } from "@/context/CastingContext";
import { useLanguage } from "@/context/LanguageContext";

export const CastingBar = () => {
  const { board, remove, clear } = useCasting();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex flex-col items-center gap-3 px-4">
      <AnimatePresence>
        {open && board.length > 0 && (
          <motion.div
            data-testid="casting-board-panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto w-full max-w-xl border border-white/15 bg-[#121212]/95 p-4 backdrop-blur-xl"
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                {t("casting.shortlist")} — {board.length} {t("casting.selected")}
              </span>
              <button
                data-testid="casting-board-clear"
                onClick={clear}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-white"
              >
                {t("casting.clear")}
              </button>
            </div>
            <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
              {board.map((m) => (
                <span
                  key={m.id}
                  className="flex items-center gap-2 border border-white/15 bg-black/40 py-1.5 pl-1.5 pr-2"
                >
                  <img src={m.image_url} alt="" className="h-7 w-6 object-cover object-top" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white">
                    {m.name}
                  </span>
                  <button
                    data-testid={`casting-board-remove-${m.id}`}
                    aria-label={`${t("casting.remove")} ${m.name}`}
                    onClick={() => remove(m.id)}
                  >
                    <X className="h-3 w-3 text-white/50 transition-colors hover:text-gold" />
                  </button>
                </span>
              ))}
            </div>
            <Link
              data-testid="casting-board-proceed"
              to="/clients"
              onClick={() => setOpen(false)}
              className="mt-4 flex items-center justify-center gap-2 bg-gold px-4 py-3 font-mono text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-ivory"
            >
              {t("casting.submit")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {board.length > 0 && (
          <motion.button
            data-testid="casting-board-toggle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={() => setOpen(!open)}
            className="pointer-events-auto flex items-center gap-3 border border-gold/60 bg-black/80 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.3em] text-gold backdrop-blur-xl transition-colors hover:bg-gold hover:text-black"
          >
            <Star className="h-3.5 w-3.5" /> {t("casting.shortlist")} ({board.length})
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};
